import React, { useCallback, useState, useEffect, useRef } from "react";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import CustomTable from "@/shared/components/ui/Button/CustomTable";
import { CampaignLeadsColumns } from "@/shared/utils/utils";
import ProfileModal from "@/features/profiling/components/Profiling/ProfileModal";
import useProfileNavigation from "@/features/profiling/components/Profiling/useProfileNavigation";
import { AlertCircle } from "lucide-react";
import { useMainStore } from "@/stores";
import Loader from "@/shared/components/ui/Loaders/Loader";
import {
  useDeleteLead,
  useFetchCampaignLeads,
} from "@/features/campaigns/hooks/useCampaign";
import { useCampaignId } from "@/features/campaigns/hooks/useCampaignId";
import NoDataTable from "@/shared/components/ui/NoDataView/NoDataTable";
import NoDataCommonCard from "@/shared/components/ui/NoDataView/NoDataCommon";
import NoDataFound from "@/shared/components/ui/NoDataView/NoDataFound";
import { useSearchParams } from "react-router-dom";
import { buildURLFromFilters } from "@/shared/utils/filterUrl";
import noLeadsImage from "@/assets/images/onboarding3.gif";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import CampaignLeadsHeader from "./CampaignLeadsHeader";
import ContactsListModal from "./components/ContactsListModal";
import { createLeadsRenderCell } from "./leadsRenderCell";

const CampaignLeadsContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInternalURLUpdate = useRef(false);
  const isReadingFromURL = useRef(false);
  const isFirstRender = useRef(true);

  const [searchValue, setSearchValue] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const user = useMainStore((state) => state.user);
  const campaignId = useCampaignId();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { mutate: deleteLead, isPending: isDeletingLead } = useDeleteLead();

  const {
    data: leadsData,
    isLoading: IsLeadsLoading,
    isFetching: isFetchingLeads,
    isError: isLeadsError,
    error: leadsError,
    refetch: refetchLeads,
  } = useFetchCampaignLeads({
    campaignId,
    page: currentPage,
    limit: rowsPerPage,
    searchQuery: searchValue,
  });

  const leads = leadsData?.leads ?? [];
  const totalCount = leadsData?.total ?? 0;

  const totalPages =
    rowsPerPage > 0 ? Math.max(1, Math.ceil(totalCount / rowsPerPage)) : 1;

  useEffect(() => {
    if (isFetchingLeads) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, isFetchingLeads]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, rowsPerPage, campaignId]);

  const prevLeadSearchRef = useRef(searchValue);
  useEffect(() => {
    if (prevLeadSearchRef.current !== searchValue && selectedLeads.length > 0) {
      setSelectedLeads([]);
    }
    prevLeadSearchRef.current = searchValue;
  }, [searchValue, selectedLeads.length]);

  // ── URL → State (mount, back/forward, manual edit) ──
  useEffect(() => {
    if (isInternalURLUpdate.current) {
      isInternalURLUpdate.current = false;
      return;
    }
    isReadingFromURL.current = true;
    const urlSearch = searchParams.get("search") || "";
    setSearchValue(urlSearch);
  }, [searchParams]);

  // ── State → URL ──
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isReadingFromURL.current) {
      isReadingFromURL.current = false;
      return;
    }
    isInternalURLUpdate.current = true;
    const queryString = buildURLFromFilters({
      filters: {},
      searchValue,
    });
    setSearchParams(queryString, { replace: true });
  }, [searchValue, setSearchParams]);

  const onSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const displayData = leads ?? [];

  const {
    selectedId,
    isModalOpen,
    isLoading,
    currentProfile,
    openProfile,
    closeProfile,
    goToNextProfile,
    goToPreviousProfile,
    fetchProfileData,
    handleProfileNavigation,
    allProfileIds,
  } = useProfileNavigation(displayData, {
    idParam: "profile",
    updateUrl: true,
    userId: user?.id,
    campaignId,
  });

  const handleSelectionChange = useCallback((selected) => {
    setSelectedLeads(selected || []);
  }, []);

  const handleDeleteShowConfirmModal = useCallback((id) => {
    setShowDeleteConfirmModal(true);
    setDeletingLeadId(id);
    setIsBulkDelete(false);
  }, []);

  const handleBulkDeleteShowConfirmModal = useCallback(() => {
    if (selectedLeads.length === 0) return;
    setShowDeleteConfirmModal(true);
    setIsBulkDelete(true);
  }, [selectedLeads.length]);

  const handleDelete = useCallback(() => {
    if (isBulkDelete && selectedLeads.length > 0) {
      const ids = selectedLeads.map((l) => l.id).filter(Boolean);
      if (ids.length > 0) {
        deleteLead(ids);
        setSelectedLeads([]);
      }
    } else if (deletingLeadId) {
      deleteLead(deletingLeadId);
    }
    setShowDeleteConfirmModal(false);
    setDeletingLeadId(null);
    setIsBulkDelete(false);
  }, [deleteLead, deletingLeadId, isBulkDelete, selectedLeads]);

  const handleClearSelection = useCallback(() => setSelectedLeads([]), []);

  const renderCell = useCallback(
    (item, columnKey) =>
      createLeadsRenderCell(openProfile, handleDeleteShowConfirmModal)(
        item,
        columnKey,
      ),
    [openProfile, handleDeleteShowConfirmModal],
  );

  const hasSearch = searchValue.trim().length > 0;
  const showSearchAndContent = displayData.length > 0 || hasSearch;

  return (
    <>
      {IsLeadsLoading && !hasSearch ? (
        <div className="w-full h-[calc(100dvh-245px)] flex items-center justify-center">
          <Loader text="Loading leads..." />
        </div>
      ) : isLeadsError ? (
        <div className="w-full h-[calc(100dvh-245px)] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 max-w-md p-6 bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800">
            <div className="w-12 h-12 rounded-full bg-error-50 flex items-center justify-center">
              <AlertCircle size={24} className="text-error-500" />
            </div>
            <div className="text-center">
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                Unable to load leads
              </h3>
              <p className="text-sm text-gray-500">
                {leadsError?.message ||
                  "Something went wrong. Please try again."}
              </p>
            </div>
            <PrimaryButton onClick={() => refetchLeads()}>
              Try again
            </PrimaryButton>
          </div>
        </div>
      ) : !showSearchAndContent ? (
        <div className="w-full relative">
          <NoDataTable variant="campaigns" />
          <NoDataCommonCard
            title="No Leads Assigned"
            description="No leads assigned to this campaign"
            buttonText="Assign Leads"
            onClick={() => setShowContactPicker(true)}
            image={noLeadsImage}
          />
        </div>
      ) : (
        <div className="flex flex-col w-full gap-0.5">
          <CampaignLeadsHeader
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSearchChange={onSearchChange}
            onAddLeads={() => setShowContactPicker(true)}
            selectedLeads={selectedLeads}
            onBulkDelete={handleBulkDeleteShowConfirmModal}
            onClearSelection={handleClearSelection}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
          />

          {IsLeadsLoading && hasSearch ? (
            <div className="flex items-center justify-center h-[calc(100dvh-245px)]">
              <Loader text="Loading leads..." />
            </div>
          ) : hasSearch && displayData.length === 0 && totalCount === 0 ? (
            <NoDataFound
              title="No Leads Found"
              description="No leads found for the search term"
              searchValue={searchValue}
            />
          ) : (
            <div className="mt-2">
              <CustomTable
                renderCell={renderCell}
                columns={CampaignLeadsColumns}
                data={displayData}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalCount={totalCount}
                page={currentPage}
                onPageChange={setCurrentPage}
                enableSelection={true}
                selectedItems={selectedLeads}
                setSelectedItems={setSelectedLeads}
                height="calc(100dvh - 193px)"
                pl="pl-3"
                pr="pr-2"
                pinFirstColumn
                showCellBorders
                onSelectionChange={handleSelectionChange}
              />
            </div>
          )}
        </div>
      )}

      <ProfileModal
        isOpen={isModalOpen}
        onClose={closeProfile}
        selectedId={selectedId}
        isLoading={isLoading}
        fetchProfileData={fetchProfileData}
        allIds={allProfileIds}
        skipFetch
        onNavigate={handleProfileNavigation}
        goToNextProfile={goToNextProfile}
        goToPreviousProfile={goToPreviousProfile}
        profileData={currentProfile}
        contextData={{
          selectedProspect:
            displayData?.find((p) => (p?.contact_id ?? p?.id) === selectedId) ||
            null,
          response: [],
        }}
      />

      <ContactsListModal
        isOpen={showContactPicker}
        onClose={() => setShowContactPicker(false)}
        campaignId={campaignId}
      />

      {showDeleteConfirmModal && (
        <DeleteConfirmationModal
          showConfirmDelete={showDeleteConfirmModal}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setDeletingLeadId(null);
            setIsBulkDelete(false);
          }}
          title={
            isBulkDelete && selectedLeads.length > 1
              ? `Are you sure you want to delete ${selectedLeads.length} leads?`
              : "Are you sure you want to delete this lead?"
          }
          description={
            isBulkDelete && selectedLeads.length > 1
              ? `Deleting ${selectedLeads.length} leads will permanently erase them from the campaign`
              : "Deleting this lead will permanently erase this lead from the campaign"
          }
          onDelete={handleDelete}
          deleteButtonText={
            isBulkDelete && selectedLeads.length > 1
              ? `Yes, delete ${selectedLeads.length} leads`
              : "Yes, delete lead"
          }
          cancelButtonText="I changed my mind"
          width="450px"
          isDeleting={isDeletingLead}
        />
      )}
    </>
  );
};

export default CampaignLeadsContent;
